// Additional fields (N6, 2026-10-14): the field types and how a value travels between the API
// and a form control. Pure; FieldValuesForm.jsx renders it, FieldDefinitionEditor.jsx (Field
// templates and a project's Field settings) names the types with it.
//
// The stored forms are the backend's (backend/unio/validation/field_values.py):
//   short_text, long_text, url, date (YYYY-MM-DD), single_choice   string
//   number          the decimal as a string ("12.50"); a JSON number is accepted too
//   boolean         true / false
//   multi_choice    [option, ...] in the options' order
//   free_list       [string, ...]
// and null means "no value". In a form a draft holds a string for the text-like types, "",
// "yes" or "no" for a boolean, and an array for the two list types.

export const FIELD_TYPES = [
  { value: "short_text", label: "Short text" },
  { value: "long_text", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Yes/no" },
  { value: "url", label: "URL" },
  { value: "single_choice", label: "Single choice" },
  { value: "multi_choice", label: "Multi choice" },
  { value: "free_list", label: "Free list" },
];
export const CHOICE_TYPES = ["single_choice", "multi_choice"];
export const LIST_TYPES = ["multi_choice", "free_list"];

export const FIELD_TARGETS = [
  { value: "project", label: "Project", plural: "Project fields" },
  { value: "study", label: "Study", plural: "Study fields" },
  { value: "dataset", label: "Dataset", plural: "Dataset fields" },
];

export const typeLabel = t => (FIELD_TYPES.find(x => x.value === t) || { label: t }).label;
export const targetLabel = t => (FIELD_TARGETS.find(x => x.value === t) || { label: t }).label;

export function isEmptyValue(v) {
  return v === null || v === undefined || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && v.length === 0);
}

/** The stored value -> what the control holds. */
export function toDraft(fieldType, value) {
  if (LIST_TYPES.includes(fieldType)) return Array.isArray(value) ? value.map(String) : [];
  if (fieldType === "boolean") return value === true ? "yes" : value === false ? "no" : "";
  return value === null || value === undefined ? "" : String(value);
}

/** What the control holds -> the value sent (null for "no value"). */
export function fromDraft(fieldType, draft) {
  if (fieldType === "multi_choice") return draft && draft.length ? draft : null;
  if (fieldType === "free_list") {
    const items = (draft || []).map(s => s.trim()).filter(Boolean);
    return items.length ? items : null;
  }
  if (fieldType === "boolean") return draft === "yes" ? true : draft === "no" ? false : null;
  const s = (draft ?? "").trim();
  return s ? s : null;
}

/** The stored value in the form fromDraft gives, so the two compare. */
function normalized(fieldType, value) {
  return fromDraft(fieldType, toDraft(fieldType, value));
}

/** {key: value} of the fields whose draft differs from the stored value (what a Save sends). */
export function changedValues(fields, drafts) {
  const out = {};
  for (const f of fields) {
    if (!(f.key in drafts)) continue;
    const next = fromDraft(f.field_type, drafts[f.key]);
    if (JSON.stringify(next) !== JSON.stringify(normalized(f.field_type, f.value))) out[f.key] = next;
  }
  return out;
}

/** A stored value as one line of text for a read-only view. */
export function formatValue(fieldType, value) {
  if (isEmptyValue(value)) return "—";
  if (fieldType === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}
